import type { RobotStatus } from '../hooks/useRobots.ts';
import { STATUS_STATE, type Tone } from './state.ts';
import { StatusDot } from './StatusDot.tsx';
import styles from './StatusBadge.module.css';

/* ==========================================================================
   Operational status: a 6px dot and a word.

   The word only takes the state colour when the state is exceptional (error,
   maintenance). Active/idle/charging stay in --text-mid so a healthy fleet
   reads greyscale and a fault is the only coloured word on the screen.
   ========================================================================== */

const LOUD: ReadonlySet<RobotStatus> = new Set<RobotStatus>(['error', 'maintenance']);

export function StatusBadge({
  status,
  mono = false,
}: {
  status: RobotStatus;
  /** Set the label in mono when it is showing a raw enum, not a curated word. */
  mono?: boolean | undefined;
}) {
  const s = STATUS_STATE[status];
  return (
    <span
      className={styles.badge}
      data-tone={s.tone}
      data-loud={LOUD.has(status)}
      data-mono={mono}
      title={s.hint}
    >
      <StatusDot tone={s.tone} hollow={s.hollow} />
      <span className={styles.label}>{s.label}</span>
    </span>
  );
}

/**
 * The same treatment for any other enum the API hands us (command status,
 * scan processing state) — caller supplies the tone so the mapping stays
 * explicit at the call site rather than guessed from a string.
 */
export function StateLabel({
  tone,
  label,
  loud = false,
  mono = true,
  hollow = false,
  title,
}: {
  tone: Tone;
  label: string;
  loud?: boolean | undefined;
  mono?: boolean | undefined;
  hollow?: boolean | undefined;
  title?: string | undefined;
}) {
  return (
    <span
      className={styles.badge}
      data-tone={tone}
      data-loud={loud}
      data-mono={mono}
      title={title}
    >
      <StatusDot tone={tone} hollow={hollow} />
      <span className={styles.label}>{label}</span>
    </span>
  );
}
